<template>
  <div class="count-panel">
    <div v-for="group in groupedCountItems" :key="group.name" class="count-group mb-3">
      <h4 class="text-subtitle-2 mb-1">{{ group.name }}</h4>
      <CountInput
        v-for="item in group.items"
        :key="item.id"
        :countItem="item"
        :value="localCounts[item.id]"
        @input="updateCount(item.id, $event)"
      />
    </div>
    <v-checkbox
      v-if="!isGroup"
      v-model="isRemoved"
      :label="photo.is_removed ? 'Display removed' : 'Remove Display'"
      :color="photo.is_removed ? 'success' : 'error'"
      dense hide-details
      class="mt-2"
    />
    <v-checkbox
      v-if="isQc || isTaggerNeedsReviewEnabled"
      v-model="localNeedsReview"
      label="Needs Review"
      dense hide-details
      class="mt-2"
    />
    <div class="mt-2 d-flex align-center">
      <v-btn
        color="primary"
        small
        :disabled="!isDirty || saving"
        @click="saveCounts"
        :loading="saving"
      >
        Save
      </v-btn>
      <v-btn
        v-if="isDirty"
        color="grey"
        small
        text
        class="ml-2"
        :disabled="saving"
        @click="cancelChanges"
      >
        Cancel
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CountInput from './CountInput.vue'

function normalizeCounts (counts) {
  // Drop empty entries (null/undefined/NaN) so dirty comparison ignores cleared inputs
  // that were never saved in the first place.
  const out = {}
  Object.entries(counts).forEach(([k, v]) => {
    if (v === null || v === undefined || (typeof v === 'number' && isNaN(v))) return
    out[String(k)] = Number(v)
  })
  return out
}

function countsEqual (a, b) {
  const na = normalizeCounts(a)
  const nb = normalizeCounts(b)
  const keysA = Object.keys(na)
  const keysB = Object.keys(nb)
  if (keysA.length !== keysB.length) return false
  return keysA.every(k => na[k] === nb[k])
}

export default {
  name: 'CountPanel',
  components: { CountInput },
  props: {
    photo: {
      type: Object,
      required: true
    },
    isQc: {
      type: Boolean,
      default: false
    },
    isGroup: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localCounts: {},
      initialCountItemIds: new Set(),
      isRemoved: false,
      localNeedsReview: false,
      saving: false,
      savedSnapshot: {
        counts: {},
        isRemoved: false,
        needsReview: false
      }
    }
  },
  computed: {
    ...mapGetters(['getCountItemsByCategory', 'isTaggerNeedsReviewEnabled']),
    groupedCountItems() {
      return this.getCountItemsByCategory
    },
    // Needs Review is editable in QC, or for taggers on an opted-in tenant.
    canEditNeedsReview() {
      return this.isQc || this.isTaggerNeedsReviewEnabled
    },
    isDirty() {
      if (!countsEqual(this.localCounts, this.savedSnapshot.counts)) return true
      if (this.isRemoved !== this.savedSnapshot.isRemoved) return true
      if (this.canEditNeedsReview && this.localNeedsReview !== this.savedSnapshot.needsReview) return true
      return false
    }
  },
  watch: {
    // Re-init only when the underlying photo identity changes; a deep watch would
    // clobber in-progress edits whenever the store object mutates.
    'photo.id': {
      handler() {
        this.initCounts(this.photo)
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['submitCounts', 'submitGroupCounts']),
    initCounts(photo) {
      const counts = {}
      const initialIds = new Set()
      if (photo && photo.count_entries) {
        photo.count_entries.forEach(entry => {
          counts[entry.count_item_id] = entry.value
          initialIds.add(entry.count_item_id)
        })
      }
      this.localCounts = counts
      this.initialCountItemIds = initialIds
      this.isRemoved = !!(photo && photo.is_removed)
      this.localNeedsReview = !!(photo && photo.needs_review)
      this.savedSnapshot = {
        counts: { ...counts },
        isRemoved: this.isRemoved,
        needsReview: this.localNeedsReview
      }
    },
    updateCount(countItemId, value) {
      this.$set(this.localCounts, countItemId, value)
    },
    cancelChanges() {
      this.localCounts = { ...this.savedSnapshot.counts }
      this.isRemoved = this.savedSnapshot.isRemoved
      this.localNeedsReview = this.savedSnapshot.needsReview
    },
    buildCountsPayload() {
      return Object.entries(this.localCounts)
        .map(([countItemId, value]) => {
          const id = parseInt(countItemId)
          const isEmpty = value === null || value === undefined || isNaN(value)
          if (isEmpty) {
            return this.initialCountItemIds.has(id)
              ? { count_item_id: id, value: null }
              : null
          }
          return { count_item_id: id, value: value }
        })
        .filter(entry => entry !== null)
    },
    refreshSnapshotFromResponse(updated) {
      // After save, server is the source of truth. Rebuild snapshot + initial IDs
      // from the response so further edits compare against the just-saved state.
      const newCounts = {}
      const newInitial = new Set()
      if (updated && updated.count_entries) {
        updated.count_entries.forEach(entry => {
          newCounts[entry.count_item_id] = entry.value
          newInitial.add(entry.count_item_id)
        })
      }
      this.localCounts = newCounts
      this.initialCountItemIds = newInitial
      this.isRemoved = !!(updated && updated.is_removed)
      this.localNeedsReview = !!(updated && updated.needs_review)
      this.savedSnapshot = {
        counts: { ...newCounts },
        isRemoved: this.isRemoved,
        needsReview: this.localNeedsReview
      }
    },
    async saveCounts() {
      this.saving = true
      const counts = this.buildCountsPayload()

      try {
        let updated
        if (this.isGroup) {
          updated = await this.submitGroupCounts({
            group_id: this.photo.id,
            counts: counts,
            is_qc: this.isQc
          })
        } else {
          const payload = {
            photo_id: this.photo.id,
            counts: counts,
            is_qc: this.isQc,
            remove: this.isRemoved
          }
          if (this.canEditNeedsReview) {
            payload.needs_review = this.localNeedsReview
          }
          updated = await this.submitCounts(payload)
        }
        if (updated) {
          this.refreshSnapshotFromResponse(updated)
        }
        this.$emit('saved', updated)
        return updated
      } finally {
        this.saving = false
      }
    },
    // Public API for parent views to auto-save before page-level submit.
    // Resolves on success or when not dirty; rejects with photo identity on failure.
    async saveIfDirty() {
      if (!this.isDirty) return { photoId: this.photo.id, ok: true, skipped: true }
      try {
        await this.saveCounts()
        return { photoId: this.photo.id, ok: true }
      } catch (error) {
        const err = new Error('CountPanel save failed')
        err.photoId = this.photo.id
        err.responseId = this.photo.response_id
        err.cause = error
        throw err
      }
    }
  }
}
</script>

<style scoped>
.count-panel {
  padding: 8px;
}
.count-group {
  border-left: 3px solid var(--v-primary-base);
  padding-left: 12px;
}
</style>
